import {
  Alert,
  Box,
  Typography,
  Grid,
  CircularProgress,
  Stack,
  Paper,
  alpha,
  Autocomplete,
  TextField,
  Button,
  IconButton,
  Tooltip,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from '@mui/material';
import { Refresh } from '@mui/icons-material';
import { useMemo, useEffect, useState, useCallback } from 'react';
import { useSelector, useDispatch } from 'src/store';
import { httpsCallable } from 'firebase/functions';
import { doc, updateDoc, arrayUnion, arrayRemove, collection, getDocs, getDoc, runTransaction, Timestamp, deleteField } from 'firebase/firestore';
import { functions, db, auth } from 'src/firebase/firebaseConfig';
import { useNotifications } from '@toolpad/core/useNotifications';
import { DayliteContact, ContactRole } from '../../../../shared/types/dayliteModels';
import { FormTemplate, ClientField, ReminderOption, AutoPopulateSource } from '../../../../shared/types/appTypes';
import { REMINDER_OPTIONS } from 'src/constants/appConstants';
import { deriveEventDate, formatEventDateForMessage } from 'src/utils/eventDate';
import { updateEventMainData } from 'src/store/slices/activeEventSlice';
import useClientNotifications from 'src/hooks/useClientNotifications';
import ClientCard from './ClientCard';
import ClientFormSummary from './ClientFormSummary';

// Helper to extract contact ID from self path
const extractIdFromPath = (path: string): string => {
  const parts = path.split('/');
  return parts[parts.length - 1];
};

interface ContactWithData {
  id: string;
  role?: string;
  data: DayliteContact | null;
  isLoading: boolean;
}

export default function SingleEventClients() {
  const dispatch = useDispatch();
  const notifications = useNotifications();
  const { dayliteData, id: eventId, mainData, isLoading, error } = useSelector((state) => state.activeEvent);
  
  const [contactsData, setContactsData] = useState<Record<string, DayliteContact>>({});
  const [isLoadingContacts, setIsLoadingContacts] = useState(false);
  const [updatingId, setUpdatingId] = useState<string | null>(null);

  // Form template state
  const [formTemplates, setFormTemplates] = useState<FormTemplate[]>([]);
  const [selectedTemplate, setSelectedTemplate] = useState<FormTemplate | null>(null);
  const [currentFormTemplateId, setCurrentFormTemplateId] = useState<string | null>(null);
  const [isLoadingTemplates, setIsLoadingTemplates] = useState(false);
  const [isSavingForm, setIsSavingForm] = useState(false);

  // Reminder state
  const [currentFormReminder, setCurrentFormReminder] = useState<ReminderOption | undefined>(undefined);
  const [selectedReminder, setSelectedReminder] = useState<ReminderOption | undefined>(undefined);
  const [isSavingReminder, setIsSavingReminder] = useState(false);

  // Check if form selection is dirty
  const isFormDirty = useMemo(() => {
    if (!selectedTemplate && !currentFormTemplateId) return false;
    if (selectedTemplate && !currentFormTemplateId) return true;
    if (!selectedTemplate && currentFormTemplateId) return true;
    return selectedTemplate?.id !== currentFormTemplateId;
  }, [selectedTemplate, currentFormTemplateId]);

  // Get client access array from mainData
  const clientAccess = useMemo(() => {
    return mainData?.clientAccess || [];
  }, [mainData?.clientAccess]);

  // Get client skip reminder array from mainData
  const clientSkipReminder = useMemo(() => {
    return mainData?.clientSkipReminder || [];
  }, [mainData?.clientSkipReminder]);

  // Filter contacts with client roles
  const clientContacts = useMemo(() => {
    if (!dayliteData?.contacts) return [];
    return dayliteData.contacts.filter(
      (contact: ContactRole) =>
        typeof contact.role === 'string' &&
        ['Client', 'Planner', 'Bride', 'Groom'].includes(contact.role)
    );
  }, [dayliteData?.contacts]);

  // Extract all contact IDs
  const contactIds = useMemo(() => {
    return clientContacts.map((c: ContactRole) => extractIdFromPath(c.contact));
  }, [clientContacts]);

  // Fetch form templates and current event's form
  useEffect(() => {
    const fetchTemplatesAndCurrentForm = async () => {
      setIsLoadingTemplates(true);
      try {
        // Fetch all templates
        const templatesRef = collection(db, 'templateClientForms');
        const templatesSnapshot = await getDocs(templatesRef);
        const templatesData: FormTemplate[] = [];
        templatesSnapshot.forEach((docSnap) => {
          templatesData.push({ id: docSnap.id, ...docSnap.data() } as FormTemplate);
        });
        setFormTemplates(templatesData);

        // Fetch current event's form to get the template ID
        if (eventId) {
          const clientFormRef = doc(db, 'clientForms', eventId);
          const clientFormSnap = await getDoc(clientFormRef);
          if (clientFormSnap.exists()) {
            const formData = clientFormSnap.data();
            const templateId = formData?.templateId;
            setCurrentFormTemplateId(templateId || null);
            // Set the selected template to match
            const matchingTemplate = templatesData.find(t => t.id === templateId);
            setSelectedTemplate(matchingTemplate || null);
            // Set reminder from existing form or fallback to template default
            const formReminder = formData?.reminder as ReminderOption | undefined;
            setCurrentFormReminder(formReminder);
            setSelectedReminder(formReminder ?? matchingTemplate?.reminder);
          }
        }
      } catch (error) {
        console.error('Error fetching templates:', error);
        notifications.show('Failed to load form templates', { severity: 'error' });
      } finally {
        setIsLoadingTemplates(false);
      }
    };

    fetchTemplatesAndCurrentForm();
  }, [eventId, notifications]);

  // Handle setting the form template for this event
  const handleSetFormTemplate = useCallback(async () => {
    if (!eventId || !selectedTemplate) return;

    setIsSavingForm(true);
    try {
      const clientFormRef = doc(db, 'clientForms', eventId);
      const now = Timestamp.now();
      const userEmail = auth.currentUser?.email || '';

      const eventRef = doc(db, 'events', eventId);

      // Resolve an auto-populate value from the event doc read inside the transaction below. Never from
      // the rendered Redux/mainData copy: the date can change while this handler is awaiting (P2-06).
      const resolveAutoPopulate = (eventData: Record<string, any>, source: AutoPopulateSource, fieldType: string): any => {
        let raw: string | undefined;
        switch (source) {
          case 'friendlyTitle': raw = eventData.friendlyTitle; break;
          case 'venueInfo': raw = eventData.venueInfo; break;
          case 'timingInfo': raw = eventData.timingInfo; break;
          case 'dayliteData.forecasted':
            raw = eventData.dateISO8601 ?? deriveEventDate(eventData.dayliteData?.forecasted) ?? undefined;
            break;
        }
        if (!raw) return undefined;
        if (source === 'dayliteData.forecasted') {
          const isoDate = deriveEventDate(raw);
          if (!isoDate) return undefined;
          if (fieldType === 'date') {
            // UTC noon of the canonical date renders as the right day from UTC-11 to UTC+11.
            return Timestamp.fromMillis(Date.UTC(Number(isoDate.slice(0, 4)), Number(isoDate.slice(5, 7)) - 1, Number(isoDate.slice(8, 10)), 12));
          }
          // Text fields get the same wording the server sync writes ("Sat, Mar 13, 2027").
          return formatEventDateForMessage(isoDate);
        }
        return raw;
      };

      // Same equality the server date sync uses to decide a field is untouched (clientFormDateSyncLogic.ts).
      const sameValue = (a: unknown, b: unknown): boolean => {
        if (a instanceof Timestamp && b instanceof Timestamp) return a.toMillis() === b.toMillis();
        return JSON.stringify(a) === JSON.stringify(b);
      };

      // Read and write in one transaction so a server-side change made meanwhile (e.g. the event-date
      // sync) is the baseline that gets merged, not a stale copy.
      await runTransaction(db, async (tx) => {
        const existingSnap = await tx.get(clientFormRef);
        const existingData = existingSnap.exists() ? existingSnap.data() : null;
        // The current event doc is a transactional read, so a date change committed meanwhile forces a
        // retry that re-derives every auto-filled value from the new snapshot.
        const eventSnap = await tx.get(eventRef);
        const eventData: Record<string, any> = eventSnap.exists() ? eventSnap.data() : {};

        // Build fields array - merge with existing values if available
        const existingFields: ClientField[] = existingData?.fields || [];

        const newFields = selectedTemplate.fields.map((templateField, idx) => {
          // Find existing field by label AND type match only — no index fallback
          // to prevent cross-type value contamination (e.g. playlist value in a time field)
          const existingField = existingFields.find(
            (ef) => ef.label === templateField.label && ef.type === templateField.type
          );

          // Only keep existing value if it's not empty/null
          const hasExistingValue = existingField && 'value' in existingField && (
            (existingField.value !== null && existingField.value !== '' && existingField.value !== undefined) ||
            (typeof existingField.value === 'object' && existingField.value !== null && 'start' in existingField.value && (existingField.value as { start: unknown }).start !== null)
          );

          const base = {
            ...templateField,
            order: idx,
            modifiedAt: now,
            modifiedBy: userEmail,
          };
          if (!hasExistingValue || !existingField || templateField.type === 'video') {
            return base;
          }

          const templateSource = templateField.autoPopulateFrom;
          const sameSource = (existingField.autoPopulateFrom ?? undefined) === (templateSource ?? undefined);
          const hasBaseline = existingField.autoPopulatedValue !== undefined && existingField.autoPopulatedValue !== null;
          const untouchedAuto = hasBaseline && sameValue(existingField.value, existingField.autoPopulatedValue);

          // The source was intentionally changed and the old value was never edited: let the new source fill it.
          if (!sameSource && untouchedAuto && templateSource) return base;

          // Preserve the answer with its edit metadata. The auto-populate baseline and confirmation are kept
          // only while the source is unchanged, so an untouched date keeps following the event (F15).
          return {
            ...base,
            value: existingField.value,
            modifiedAt: existingField.modifiedAt ?? now,
            modifiedBy: existingField.modifiedBy ?? userEmail,
            ...(existingField.editHistory ? { editHistory: existingField.editHistory } : {}),
            ...(sameSource && hasBaseline ? { autoPopulatedValue: existingField.autoPopulatedValue } : {}),
            ...(sameSource && existingField.clientConfirmed !== undefined ? { clientConfirmed: existingField.clientConfirmed } : {}),
          };
        });

        // Auto-populate empty fields from event data
        newFields.forEach((field) => {
          if (field.type === 'video' || !field.autoPopulateFrom) return;
          // Only fill empty fields
          const isEmpty = !('value' in field) || field.value == null || field.value === '' ||
            (Array.isArray(field.value) && field.value.length === 0);
          if (!isEmpty) return;
          const resolved = resolveAutoPopulate(eventData, field.autoPopulateFrom, field.type);
          if (resolved !== undefined) {
            (field as any).value = resolved;
            (field as any).autoPopulatedValue = resolved;
          }
        });

        const formData: any = {
          templateId: selectedTemplate.id,
          templateName: selectedTemplate.name,
          fields: newFields,
          updatedAt: now,
          updatedBy: userEmail,
          ...(existingData ? {} : { createdAt: now, createdBy: userEmail }),
        };

        // Only include reminder field if it has a value, otherwise delete it
        if (selectedReminder) {
          formData.reminder = selectedReminder;
        } else if (existingData) {
          formData.reminder = deleteField();
        }

        tx.set(clientFormRef, formData, { merge: true });
      });

      setCurrentFormTemplateId(selectedTemplate.id);
      setCurrentFormReminder(selectedReminder);
      notifications.show('Form template set successfully', { severity: 'success', autoHideDuration: 3000 });
    } catch (error) {
      console.error('Error setting form template:', error);
      notifications.show('Failed to set form template', { severity: 'error' });
    } finally {
      setIsSavingForm(false);
    }
  }, [eventId, selectedTemplate, selectedReminder, notifications]);

  // Handle saving just the reminder (without changing template)
  const handleSaveReminder = useCallback(async () => {
    if (!eventId || !currentFormTemplateId) return;

    setIsSavingReminder(true);
    try {
      const clientFormRef = doc(db, 'clientForms', eventId);
      const now = Timestamp.now();
      const userEmail = auth.currentUser?.email || '';

      await updateDoc(clientFormRef, {
        reminder: selectedReminder ? selectedReminder : deleteField(),
        updatedAt: now,
        updatedBy: userEmail,
      });

      setCurrentFormReminder(selectedReminder);
      notifications.show('Reminder updated', { severity: 'success', autoHideDuration: 2000 });
    } catch (error) {
      console.error('Error saving reminder:', error);
      notifications.show('Failed to update reminder', { severity: 'error' });
    } finally {
      setIsSavingReminder(false);
    }
  }, [eventId, currentFormTemplateId, selectedReminder, notifications]);

  // Fetch contact details
  useEffect(() => {
    const fetchContacts = async () => {
      if (contactIds.length === 0) return;

      // Filter out IDs we already have
      const idsToFetch = contactIds.filter((id: string) => !contactsData[id]);
      if (idsToFetch.length === 0) return;

      setIsLoadingContacts(true);
      try {
        const fetchContactsByIdsFn = httpsCallable(functions, 'fetchContactsByIds');
        const result = await fetchContactsByIdsFn({ ids: idsToFetch });
        const response = result.data as {
          success: boolean;
          contacts: Array<{ id: string; data: DayliteContact }>;
          errors: Array<{ id: string; error: string }>;
        };

        if (response.success) {
          const newContactsData: Record<string, DayliteContact> = {};
          response.contacts.forEach(({ id, data }) => {
            newContactsData[id] = data;
          });
          setContactsData((prev) => ({ ...prev, ...newContactsData }));

          // Log any errors
          if (response.errors.length > 0) {
            console.warn('Some contacts failed to load:', response.errors);
          }
        }
      } catch (error) {
        console.error('Error fetching contacts:', error);
        notifications.show('Failed to load contact details', { severity: 'error' });
      } finally {
        setIsLoadingContacts(false);
      }
    };

    fetchContacts();
  }, [contactIds]); // Only re-run when contactIds change

  // Handle toggle access
  const handleToggleAccess = useCallback(
    async (contactId: string, enabled: boolean) => {
      if (!eventId) return;

      setUpdatingId(contactId);
      try {
        const eventRef = doc(db, 'events', eventId);

        if (enabled) {
          await updateDoc(eventRef, {
            clientAccess: arrayUnion(contactId),
          });
        } else {
          await updateDoc(eventRef, {
            clientAccess: arrayRemove(contactId),
          });
        }

        // Update local state
        const newClientAccess = enabled
          ? [...clientAccess, contactId]
          : clientAccess.filter((id: string) => id !== contactId);

        dispatch(
          updateEventMainData({
            ...mainData,
            clientAccess: newClientAccess,
          })
        );

        notifications.show(
          enabled ? 'Client access granted' : 'Client access removed',
          { severity: 'success', autoHideDuration: 2000 }
        );
      } catch (error) {
        console.error('Error updating client access:', error);
        notifications.show('Failed to update access', { severity: 'error' });
      } finally {
        setUpdatingId(null);
      }
    },
    [eventId, clientAccess, mainData, dispatch, notifications]
  );

  // Handle toggle skip reminder
  const handleToggleSkipReminder = useCallback(
    async (contactId: string, skip: boolean) => {
      if (!eventId) return;

      setUpdatingId(contactId);
      try {
        const eventRef = doc(db, 'events', eventId);

        if (skip) {
          await updateDoc(eventRef, {
            clientSkipReminder: arrayUnion(contactId),
          });
        } else {
          await updateDoc(eventRef, {
            clientSkipReminder: arrayRemove(contactId),
          });
        }

        // Update local state
        const newClientSkipReminder = skip
          ? [...clientSkipReminder, contactId]
          : clientSkipReminder.filter((id: string) => id !== contactId);

        dispatch(
          updateEventMainData({
            ...mainData,
            clientSkipReminder: newClientSkipReminder,
          })
        );
      } catch (error) {
        console.error('Error updating skip reminder:', error);
        notifications.show('Failed to update reminder setting', { severity: 'error' });
      } finally {
        setUpdatingId(null);
      }
    },
    [eventId, clientSkipReminder, mainData, dispatch, notifications]
  );

  // Client notifications hook
  const {
    inviteRecords,
    sendingContactId,
    sendOnboardingInvite,
    hasAccount,
  } = useClientNotifications({
    eventId,
    contactsData,
    contactIds,
  });

  // Handle send invite
  const handleSendInvite = useCallback(
    async (contactId: string) => {
      await sendOnboardingInvite(contactId);
    },
    [sendOnboardingInvite]
  );

  // Build contacts with their data for rendering
  const contactsWithData: ContactWithData[] = useMemo(() => {
    return clientContacts.map((contact: ContactRole) => {
      const id = extractIdFromPath(contact.contact);
      return {
        id,
        role: contact.role,
        data: contactsData[id] || null,
        isLoading: isLoadingContacts && !contactsData[id],
      };
    });
  }, [clientContacts, contactsData, isLoadingContacts]);

  // Show loading spinner while data is being fetched or hasn't loaded yet
  if (isLoading || !dayliteData) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  // Only show error if something actually threw
  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">
          Failed to load event data: {error}
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Paper
        elevation={0}
        sx={{
          p: 2,
          mb: 2,
          bgcolor: (theme) => alpha(theme.palette.primary.main, 0.04),
          borderRadius: 2,
        }}
      >
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 600 }}>
              Client Access
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              Showing contacts with roles: Client, Planner, Bride, Groom
            </Typography>
          </Box>
          <Typography variant="body2" color="text.secondary">
            {contactsWithData.length} client{contactsWithData.length !== 1 ? 's' : ''} •{' '}
            {clientAccess.length} with access
          </Typography>
        </Stack>
      </Paper>

      {/* Form Controls */}
      <Paper
        variant="outlined"
        sx={{
          p: 2,
          mb: 3,
          borderRadius: 2,
        }}
      >
        <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap">
          <Autocomplete
            size="small"
            options={formTemplates}
            getOptionLabel={(option) => option.name}
            value={selectedTemplate}
            onChange={(_, newValue) => setSelectedTemplate(newValue)}
            loading={isLoadingTemplates}
            sx={{ width: 250 }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Client Form"
                placeholder="Select form template"
              />
            )}
            isOptionEqualToValue={(option, value) => option.id === value.id}
          />
          <Tooltip title="Apply selected template to this event">
            <span>
              <Button
                variant="contained"
                size="small"
                onClick={handleSetFormTemplate}
                disabled={!isFormDirty || isSavingForm}
                sx={{ minWidth: 60 }}
              >
                {isSavingForm ? <CircularProgress size={18} /> : 'Set'}
              </Button>
            </span>
          </Tooltip>
          <Tooltip title="Re-apply template (update form with latest template changes)">
            <span>
              <IconButton
                size="small"
                onClick={handleSetFormTemplate}
                disabled={!selectedTemplate || !currentFormTemplateId || isSavingForm}
              >
                {isSavingForm ? <CircularProgress size={18} /> : <Refresh />}
              </IconButton>
            </span>
          </Tooltip>

          {currentFormTemplateId && (
            <>
              <FormControl size="small" sx={{ minWidth: 180 }}>
                <InputLabel>Remind</InputLabel>
                <Select
                  value={selectedReminder || 'none'}
                  onChange={(e) => {
                    const value = e.target.value;
                    setSelectedReminder(value === 'none' ? undefined : value as ReminderOption);
                  }}
                  label="Remind"
                >
                  {REMINDER_OPTIONS.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              {selectedReminder !== currentFormReminder && (
                <Button
                  size="small"
                  variant="contained"
                  onClick={handleSaveReminder}
                  disabled={isSavingReminder}
                  sx={{ minWidth: 60 }}
                >
                  {isSavingReminder ? <CircularProgress size={18} /> : 'Save'}
                </Button>
              )}
            </>
          )}

          {isLoadingContacts && (
            <Stack direction="row" alignItems="center" spacing={1} sx={{ ml: 'auto' }}>
              <CircularProgress size={16} />
              <Typography variant="body2" color="text.secondary">
                Loading details...
              </Typography>
            </Stack>
          )}
        </Stack>
      </Paper>

      {/* Client Cards Grid */}
      {contactsWithData.length === 0 ? (
        <Alert severity="info">
          No clients found for this event. Add contacts with Client, Planner, Bride, or Groom
          roles in Daylite to see them here.
        </Alert>
      ) : (
        <Grid container spacing={3}>
          {contactsWithData.map((contact) => (
            <Grid item xs={12} sm={6} md={4} key={contact.id}>
              <ClientCard
                contactId={contact.id}
                role={contact.role}
                contactData={contact.data}
                isLoading={contact.isLoading}
                hasAccess={clientAccess.includes(contact.id)}
                onToggleAccess={handleToggleAccess}
                onSendInvite={handleSendInvite}
                isSendingInvite={sendingContactId === contact.id}
                inviteRecord={inviteRecords[contact.id]}
                hasAccount={hasAccount(contact.id)}
                isUpdating={updatingId === contact.id}
                skipReminder={clientSkipReminder.includes(contact.id)}
                onToggleSkipReminder={handleToggleSkipReminder}
              />
            </Grid>
          ))}
        </Grid>
      )}

      {/* Client Form Responses Summary */}
      {eventId && <ClientFormSummary eventId={eventId} />}
    </Box>
  );
}
